
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { authApi } from "./authApi";
import { SESSION_EXPIRED_EVENT } from "./cookieApi";
import {
  isAuthenticated as checkAuth,
  clearTokens,
} from "./tokenManager";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const login = useCallback(async (phone, password) => {
    const data = await authApi.login(phone, password);

    try {
      const userData = await authApi.getUser();
      setUser(userData);
    } catch (err) {
      // Credentials were accepted, but the profile could not be loaded.
      // Keep the distinction so the UI doesn't report a false login failure.
      err.loginSucceeded = true;
      throw err;
    }

    return data;
  }, []);

  const register = useCallback(async (userData) => {
    return authApi.register(userData);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      clearTokens();
      setUser(null);
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  const requestOTP = useCallback(async (phone) => {
    return authApi.requestOTP(phone);
  }, []);

  const verifyOTP = useCallback(async (phone, code) => {
    const data = await authApi.verifyOTP(phone, code);

    try {
      const userData = await authApi.getUser();
      setUser(userData);
    } catch (err) {
      err.otpVerified = true;
      throw err;
    }

    return data;
  }, []);

  const resetPassword = useCallback(async (phone) => {
    return authApi.resetPassword(phone);
  }, []);

  const resetPasswordConfirm = useCallback(
    async (uid, token, new_password) => {
      return authApi.resetPasswordConfirm(uid, token, new_password);
    },
    [],
  );

  const refreshUser = useCallback(async () => {
    try {
      const userData = await authApi.getUser();
      setUser(userData);
      return userData;
    } catch (err) {
      if (err.response?.status === 401) {
        clearTokens();
        setUser(null);
      }
      throw err;
    }
  }, []);

  useEffect(() => {
    let active = true;

    const initAuth = async () => {
      try {
        if (checkAuth()) {
          const userData = await authApi.getUser();
          if (active) setUser(userData);
        }
      } catch (err) {
        if (err.response?.status === 401) {
          clearTokens();
          if (active) setUser(null);
        }
        // Keep stored credentials on network errors or server failures.
      } finally {
        if (active) setLoading(false);
      }
    };

    initAuth();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => {
      clearTokens();
      setUser(null);
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);

    return () => {
      window.removeEventListener(
        SESSION_EXPIRED_EVENT,
        handleSessionExpired,
      );
    };
  }, []);

  const value = {
    user,
    isAuthenticated: !!user,
    isAdmin: !!user?.is_staff,
    loading,
    login,
    register,
    logout,
    requestOTP,
    verifyOTP,
    resetPassword,
    resetPasswordConfirm,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};
